// Magic Eraser tool — click to erase contiguous similar pixels (like Magic Wand + Delete).
// Samples the color at click point, selects contiguous similar pixels, then erases them.

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import { floodFillFromSeed } from "../pixel/floodFill";
import type { BackgroundLayer, Layer, RasterLayer } from "../document/Layer";
import type { BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export interface MagicEraserSettings {
  tolerance: number;
  antiAlias: boolean;
  contiguous: boolean;
  sampleAllLayers: boolean;
  opacity: number;
}

export class MagicEraserTool implements Tool {
  readonly id = "magic-eraser";
  readonly shortcut = "E";

  settings: MagicEraserSettings = {
    tolerance: 32,
    antiAlias: true,
    contiguous: true,
    sampleAllLayers: false,
    opacity: 1,
  };

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const found = findEraseTarget(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!found || !found.visible) return;
    if (found.lockAll) {
      ctx.showTip("Layer is locked.");
      return;
    }
    // A true raster layer's own Lock Image still blocks erasing. A Background
    // layer's `lockImage` flag instead represents "no transparency yet" — the
    // eraser itself is what triggers the Photoshop-style implicit conversion
    // to a normal layer below, so it must not be blocked by that flag (i83).
    if (found.kind === "raster" && found.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }
    const layer = convertBackgroundToLayer(found);

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const layerX = Math.floor(p.x - layer.x);
    const layerY = Math.floor(p.y - layer.y);

    if (layerX < 0 || layerY < 0 || layerX >= layer.pixels.width || layerY >= layer.pixels.height) return;

    const data = layer.pixels.data;
    const w = layer.pixels.width;
    const h = layer.pixels.height;
    const pi = (layerY * w + layerX) * 4;

    // If pixel is already transparent, nothing to do
    if (data[pi + 3] === 0) return;

    // Create flood fill mask based on tolerance
    const mask = floodFillFromSeed(data, w, h, layerX, layerY, {
      tolerance: this.settings.tolerance,
      contiguous: this.settings.contiguous,
      antiAlias: this.settings.antiAlias,
      sampleAllLayers: this.settings.sampleAllLayers,
    });

    // Erase pixels in the mask — first compute the bbox via a dry-run so we
    // can capture the true before-snapshot before any mutation occurs.
    const opacity = this.settings.opacity;
    let minX = w, minY = h, maxX = -1, maxY = -1;
    let touched = false;

    for (let i = 0; i < mask.length; i++) {
      const maskVal = mask[i]! / 255;
      if (maskVal <= 0) continue;

      const idx = i * 4;
      const currentAlpha = data[idx + 3]!;
      if (currentAlpha === 0) continue;

      const eraseAmount = maskVal * opacity;
      const newAlpha = Math.max(0, Math.round(currentAlpha * (1 - eraseAmount)));
      if (newAlpha < currentAlpha) {
        touched = true;
        const px = i % w;
        const py = Math.floor(i / w);
        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
      }
    }

    if (!touched) return;

    // Capture true before-snapshot BEFORE mutating any pixels.
    const bbox: BBox = { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
    const before = snapshotRegion(layer, bbox);

    // Now apply the erase mutations.
    for (let i = 0; i < mask.length; i++) {
      const maskVal = mask[i]! / 255;
      if (maskVal <= 0) continue;

      const idx = i * 4;
      const currentAlpha = data[idx + 3]!;
      if (currentAlpha === 0) continue;

      const eraseAmount = maskVal * opacity;
      const newAlpha = Math.max(0, Math.round(currentAlpha * (1 - eraseAmount)));
      data[idx + 3] = newAlpha;
    }

    layer.pixels.markDirty();
    ctx.requestRender();

    // Capture after-snapshot and emit the undoable command.
    const after = snapshotRegion(layer, bbox);
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }
}

/**
 * Local variant of LayerUtils.findRaster that also matches a "background"
 * kind layer (audit i83). `document/LayerUtils.ts` is out of scope for this
 * fix, so this duplicates its recursive-lookup shape rather than editing the
 * shared helper.
 */
function findEraseTarget(layers: readonly Layer[], id: string): RasterLayer | BackgroundLayer | null {
  for (const l of layers) {
    if (l.id === id && (l.kind === "raster" || l.kind === "background")) return l;
    if (l.kind === "group") {
      const inner = findEraseTarget(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

/**
 * Photoshop converts a Background layer to a normal layer the instant a tool
 * would introduce transparency into it (erasing, cutting, etc.) — the lock
 * icon disappears and it behaves like any other layer from then on. We
 * mirror that here by flipping `kind` to "raster" and dropping the
 * position/image locks a Background layer carries by default, in place, so
 * the erase can proceed and produce real transparency instead of being
 * blocked or silently doing nothing.
 *
 * Scope note: this mutates the layer object in place rather than going
 * through a Command. `PaintStrokeCommand`'s own layer lookup (see
 * src/core/commands/PaintStrokeCommand.ts) only matches `kind === "raster"`,
 * and that file — along with the History/Command infrastructure needed to
 * make the conversion itself a proper, separately-undoable step — is out of
 * scope for this fix (task limited to BackgroundEraserTool.ts/MagicEraserTool.ts).
 * Practically: the Background→normal conversion is not itself on the undo
 * stack (undoing the erase stroke restores pixels but the layer stays a
 * normal layer afterward), the same way live pixel mutations during a drag
 * are already applied before the PaintStrokeCommand is emitted. A fully
 * undoable conversion needs a dedicated Command and is follow-up work.
 */
function convertBackgroundToLayer(layer: RasterLayer | BackgroundLayer): RasterLayer {
  if (layer.kind === "background") {
    const mutable = layer as unknown as { kind: string; lockImage: boolean; lockPosition: boolean };
    mutable.kind = "raster";
    mutable.lockImage = false;
    mutable.lockPosition = false;
  }
  return layer as RasterLayer;
}
